import { useLogger } from '../adapters/ports.js';

import { requestPath } from './AdministrationApiError.js';

/**
 * What this client says about the requests it makes.
 *
 * One seam produces every entry, which is what makes the vocabulary worth
 * fixing here rather than leaving to each host. A desktop app measuring itself
 * against the administration service reads the same `http.request` a test does.
 */

// The service this client fronts, stamped on every entry. A host that also runs
// the dealer SDK gets two `api` values in one stream rather than one
// indistinguishable pile.
export const ADMINISTRATION_API = 'administration-server';

// A path with its identifiers collapsed, so a hundred companies are one
// endpoint rather than a hundred.
//
// Two shapes get replaced and no others: a 24-character hex identifier, which
// is every `_id` this service issues, and a four-character auction-house slug
// under `/by-slug/`. A blunter rule — every last segment — would collapse
// `/companies/search` into `/companies/:id` and hide the route that matters
// most.
export function normalizeRequestPath(url) {
    return requestPath(url)
        .replace(/\/[0-9a-f]{24}(?=\/|$)/g, '/:id')
        .replace(/\/by-slug\/[^/]+/g, '/by-slug/:slug');
}

// Milliseconds, from whatever clock the runtime has. `performance` is present in
// every browser and in Node, but a test harness may replace `globalThis` with
// less, so `Date` is the fallback rather than an assumption.
export function requestTimestamp() {
    if (typeof globalThis.performance?.now === 'function') {
        return globalThis.performance.now();
    }
    return Date.now();
}

/**
 * One entry per request, whatever happened.
 *
 * A rejected `fetch` is the offline, DNS or CORS case: no status, no response,
 * and none of the status branches run. It is also the most informative failure
 * a user on a bad connection produces, so it must not be the one that goes
 * unreported — which is why `err` is a parameter here rather than a second
 * function.
 */
export function logRequestOutcome({ method, url, startedAt, status, ok, err }) {
    const fields = {
        event: 'http.request',
        api: ADMINISTRATION_API,
        method,
        path: normalizeRequestPath(url),
        durationMs: Math.round(requestTimestamp() - startedAt)
    };
    if (status !== undefined) {
        fields.status = status;
    }
    if (err !== undefined) {
        fields.err = err;
    }
    if (ok === true) {
        useLogger().debug(fields, 'request completed');
        return fields;
    }
    useLogger().warn(fields, 'request failed');
    return fields;
}
